import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import type {
  AdminUser,
  CreateUserDto,
  UpdateUserDto,
} from '../../../types/api';

interface User {
  id: string;
  name: string;
  email?: string;
  employeeNumber: string;
  role: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: CreateUserDto | UpdateUserDto) => Promise<void>;
  user?: AdminUser | null;
  availableRoles?: Array<{ value: string; label: string }>;
  currentUserRole?: string;
  title: string;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  user,
  availableRoles,
  currentUserRole,
  title,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employeeNumber: '',
    role: 'REPRESENTATIVE' as const,
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email ?? '',
        employeeNumber: user.employeeNumber,
        role: user.role,
        password: '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        employeeNumber: '',
        role: 'REPRESENTATIVE',
        password: '',
      });
    }
    setErrors({});
  }, [user, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.employeeNumber.trim()) {
      newErrors.employeeNumber = 'El número de empleado es requerido';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'La contraseña es requerida';
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim() || undefined,
        employeeNumber: formData.employeeNumber.trim(),
        role: formData.role,
        ...(formData.password ? { password: formData.password } : {}),
      };

      await onSave(userData);
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      setErrors({ submit: 'Error al guardar usuario' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-w-md max-h-screen sm:max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {errors.submit}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                handleChange('name', e.target.value);
              }}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Nombre completo"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de empleado *
            </label>
            <input
              type="text"
              value={formData.employeeNumber}
              onChange={(e) => {
                handleChange('employeeNumber', e.target.value);
              }}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                errors.employeeNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Ej: 00001"
            />
            {errors.employeeNumber && (
              <p className="text-red-500 text-sm mt-1">
                {errors.employeeNumber}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email (opcional)
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                handleChange('email', e.target.value);
              }}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="usuario@empresa.com"
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol *
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => {
                setFormData({ ...formData, role: e.target.value });
              }}
              className="w-full px-3 py-2.5 sm:py-2 sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium"
              required
            >
              <option value="">Seleccionar rol...</option>
              {availableRoles && availableRoles.length > 0 ? (
                availableRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))
              ) : (
                <>
                  <option value="SYSTEM_ADMIN">
                    Administrador del Sistema
                  </option>
                  <option value="ADMIN">Administrador</option>
                  <option value="GENERAL_MANAGER">Gerente General</option>
                  <option value="ZONE_MANAGER">Gerente de Zona</option>
                  <option value="REPRESENTATIVE">Representante</option>
                  <option value="SELLER">Vendedora</option>
                </>
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña *'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => {
                  handleChange('password', e.target.value);
                }}
                className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={
                  isEditing
                    ? 'Dejar vacío para no cambiar'
                    : 'Mínimo 6 caracteres'
                }
              />
              <button
                type="button"
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-medium transition-colors disabled:opacity-50"
            >
              {loading
                ? 'Guardando...'
                : isEditing
                ? 'Actualizar'
                : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
